import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';

/**
 * Helper function to get CSRF token from cookies
 */
const getCsrfTokenFromCookie = (): string | null => {
    const cookieValue = document.cookie
        .split('; ')
        .find(row => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1];

    return cookieValue ? decodeURIComponent(cookieValue) : null;
};

/**
 * Helper function to ensure CSRF token exists
 */
const ensureCsrfToken = async (api: AxiosInstance): Promise<void> => {
    await api.get('/auth/csrf');
    if (!getCsrfTokenFromCookie()) {
        throw new Error('Failed to obtain CSRF token');
    }
};

/**
 * Creates and configures an Axios instance with CSRF protection
 */
export const createAxiosInstance = (): AxiosInstance => {
    //this is really only used for development
    const baseURL = import.meta.env.VITE_API_URL || '/api';

    const csrfExemptEndpoints = ['/login',
        '/reset-password',
        "/perform-reset-password",
        "/reset-token-status",
        "/change-expired-password",
        "/send-code",
        "/verify-code",
    ];

    const instance = axios.create({
        baseURL: baseURL,
        withCredentials: true,
        xsrfCookieName: 'XSRF-TOKEN',
        xsrfHeaderName: 'X-XSRF-TOKEN'
    });

    // Add CSRF protection to non-GET requests
    instance.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
        // Skip CSRF for GET requests and exempt endpoints
        if (config.method?.toLowerCase() === 'get') {
            return config;
        }
        if (csrfExemptEndpoints.some(endpoint => config.url?.endsWith(endpoint))) {
            return config;
        }

        // Handle CSRF token for non-exempt requests
        const token = getCsrfTokenFromCookie();
        if (!token) {
            await ensureCsrfToken(instance);
        }

        const newToken = getCsrfTokenFromCookie();
        if (newToken && config.headers) {
            config.headers['X-XSRF-TOKEN'] = newToken;
        }

        return config;
    });

    return instance;
};